import { __assign, __awaiter, __generator } from "tslib";
import { getUrlParam, paramNames, getUrlPath, getUrlQuery } from './urlUtils';
import * as _ from 'lodash';
import Raven from '@wix/fe-essentials-viewer-platform/raven-js';
import { getCookie } from './cookieUtils';
import { isInSEO, isMobileFriendly, renderingEnv, isLoggedIn } from './constants/constants';
import { aMemoryStorage } from './memoryStorage/memoryStorage';
import webLogger from '@wix/web-bi-logger';
var ViewerScriptHandler = /** @class */ (function () {
    function ViewerScriptHandler(viewerScript, wixAdapter, updateState, siteInfo, overrides) {
        var _this = this;
        if (overrides === void 0) { overrides = {}; }
        var _a, _b;
        this.viewerScript = viewerScript;
        this.wixAdapter = wixAdapter;
        this.overrides = overrides;
        this.warmupData = {};
        this.appParams = _.get(this.overrides, 'appParams', {});
        var currency = getUrlParam(paramNames.SITE_CURRENCY);
        var currentUser = {
            id: this.wixAdapter.getUid(),
            role: 'Admin',
            loggedIn: isLoggedIn,
            instance: this.getInstance(),
            getEmail: function () { return _this.wixAdapter.getCurrentMember().then(function (member) { return member.email; }); },
            getRoles: function () { return Promise.resolve(['Admin']); },
            getPricingPlans: function () { return Promise.resolve([]); },
        };
        this.wixAPI = {
            window: {
                openModal: this.wixAdapter.openModal.bind(this.wixAdapter),
                openLightbox: function () { return new Promise(_.noop); },
                closeWindow: this.wixAdapter.closeWindow.bind(this.wixAdapter),
                trackEvent: this.wixAdapter.trackEvent.bind(this.wixAdapter),
                openPopup: this.wixAdapter.openPopup.bind(this.wixAdapter),
                openPersistentPopup: this.wixAdapter.openPersistentPopup.bind(this.wixAdapter),
                locale: _.get(this.overrides, 'locale', this.wixAdapter.getLocale()),
                viewMode: this.wixAdapter.getViewMode(),
                formFactor: this.wixAdapter.getDeviceType(),
                isMobileFriendly: isMobileFriendly,
                multilingual: this.getMultiLingualConfig(),
                rendering: {
                    env: renderingEnv,
                    renderCycle: 1,
                },
                lightbox: {
                    getContext: _.noop,
                },
                warmupData: {
                    get: function (key) {
                        return _this.warmupData[key];
                    },
                    set: function (key, value) {
                        _this.warmupData[key] = value;
                    },
                },
                getRouterPublicData: function () {
                    var routerDataParam = getUrlParam(paramNames.ROUTER_DATA);
                    if (!routerDataParam) {
                        return null;
                    }
                    try {
                        return JSON.parse(routerDataParam);
                    }
                    catch (_a) {
                        return null;
                    }
                },
            },
            site: {
                currency: _.get(this.overrides, 'currency', currency),
                currentPage: this.wixAdapter.currentPage.call(this.wixAdapter),
                getCurrentPageInfo: this.wixAdapter.getCurrentPageInfo.bind(this.wixAdapter),
                getSectionUrl: this.wixAdapter.getSectionUrl.bind(this.wixAdapter),
                getSiteStructure: this.wixAdapter.getSiteStructure.bind(this.wixAdapter),
                setPageMetaData: this.wixAdapter.setPageMetaData.bind(this.wixAdapter),
                isAppSectionInstalled: this.wixAdapter.isAppSectionInstalled.bind(this.wixAdapter),
                getAppToken: function () { return _this.appParams.instance; },
                revision: this.wixAdapter.getSiteRevision.call(this.wixAdapter),
                language: _.get(this.overrides, 'locale', this.wixAdapter.getLocale()),
                getPublicAPI: this.wixAdapter.getPublicAPI.bind(this.wixAdapter),
                onInstanceChanged: this.wixAdapter.onInstanceChanged.bind(this.wixAdapter),
                loadNewSession: this.wixAdapter.loadNewSession.bind(this.wixAdapter),
                getCustomizedUrlSegments: this.wixAdapter.getCustomizedUrlSegments.bind(this.wixAdapter),
                timezone: this.wixAdapter.getSiteTimezone(),
            },
            user: {
                currentUser: currentUser,
                promptLogin: _.get(this.overrides, 'promptLogin', function () { return Promise.resolve(currentUser); }),
                logout: _.noop,
                promptForgotPassword: function () { return Promise.resolve(); },
                onLogin: _.get(this.overrides, 'onLogin', _.noop),
                emailUser: function () { return Promise.resolve(); },
                register: function () { return Promise.resolve(); },
                login: function () { return Promise.resolve(); },
                getCurrentConsentPolicy: this.wixAdapter.getCurrentConsentPolicy.bind(this.wixAdapter),
                _getConsentPolicyHeader: this.wixAdapter.getConsentPolicyHeader.bind(this.wixAdapter),
                applySessionToken: function () { return Promise.resolve(); },
            },
            location: {
                navigateToSection: this.wixAdapter.navigateToSection.bind(this.wixAdapter),
                baseUrl: _.get(this.overrides, 'siteBaseUrl', siteInfo.baseUrl),
                get path() {
                    return _.get(overrides, 'location.path', getUrlPath(window.location.href));
                },
                query: _.get(this.overrides, 'location.query', getUrlQuery(siteInfo.url)),
                url: siteInfo.url,
                onChange: _.get(this.overrides, 'location.onChange', _.noop),
                to: this.getLocationTo(),
                queryParams: {
                    add: _.get(this.overrides, 'location.queryParams.add', _.noop),
                    remove: _.get(this.overrides, 'location.queryParams.remove', _.noop),
                },
                buildCustomizedUrl: this.wixAdapter.buildCustomizedUrl.bind(this.wixAdapter),
                navigateTo: _.get(this.overrides, 'location.navigateTo', _.noop),
            },
            seo: {
                renderSEOTags: _.noop,
                isInSEO: function () { return isInSEO; },
                setSeoStatusCode: _.noop,
            },
            environment: {
                timers: {
                    setTimeout: function () { return 0; },
                    clearTimeout: _.noop,
                    setInterval: function () { return 0; },
                    clearInterval: _.noop,
                    queueMicrotask: _.noop,
                },
                network: {
                    importScripts: _.noop,
                    importAMDModule: function () { return Promise.resolve({}); },
                    fetch: function () { return Promise.resolve({}); },
                },
                console: {
                    log: _.noop,
                    warn: _.noop,
                    error: _.noop,
                    debug: _.noop,
                    info: _.noop,
                },
            },
            animations: {
                timeline: function () {
                    return ({
                        add: function () { return _this; },
                        play: function () { return _this; },
                        pause: function () { return _this; },
                        replay: function () { return _this; },
                        reverse: function () { return _this; },
                        onStart: function () { return 0; },
                        onComplete: function () { return 0; },
                        onRepeat: function () { return 0; },
                        onReverseComplete: function () { return 0; },
                    });
                },
            },
        };
        this.setProps = function (wixCodeProps) { return updateState(wixCodeProps); };
        this.$wWrapper = (_b = (_a = this.overrides) === null || _a === void 0 ? void 0 : _a.$w) !== null && _b !== void 0 ? _b : _.assign({
            props: function (wixCodeProps) { return updateState(wixCodeProps); },
        });
        this.appParams.instance = this.getInstance();
        var encodedInstance = this.appParams.instance ? _.split(this.appParams.instance, '.')[1] : null;
        var _c = encodedInstance
            ? JSON.parse(atob(encodedInstance))
            : { appDefId: null, instanceId: null }, appDefinitionId = _c.appDefId, instanceId = _c.instanceId;
        this.appParams.appDefinitionId = appDefinitionId;
        this.appParams.instanceId = instanceId;
        this.wixAdapter.onInstanceChanged(function (event) { return (_this.appParams = __assign(__assign({}, _this.appParams), { instance: event.instance })); });
    }
    ViewerScriptHandler.prototype.getMultiLingualConfig = function () {
        var currentLang = getUrlParam(paramNames.CURRENT_LANG);
        var locale = getUrlParam(paramNames.LOCALE);
        return {
            isEnabled: !!currentLang,
            currentLanguage: currentLang,
            siteLanguages: (currentLang && [
                {
                    countryCode: locale === null || locale === void 0 ? void 0 : locale.split('-')[1],
                    isPrimaryLanguage: getUrlParam(paramNames.IS_PRIMARY_LANG) === 'true',
                    languageCode: currentLang,
                    locale: locale,
                },
            ]) ||
                [],
        };
    };
    ViewerScriptHandler.prototype.getStorage = function () {
        var storage = { local: undefined, session: undefined, memory: undefined };
        storage.memory = aMemoryStorage;
        try {
            storage.local = window.localStorage;
            storage.session = window.sessionStorage;
        }
        catch (e) {
            storage.local = aMemoryStorage;
            storage.session = aMemoryStorage;
        }
        return storage;
    };
    ViewerScriptHandler.prototype.getLocationTo = function () {
        if (this.overrides.location) {
            return this.overrides.location.to;
        }
        return this.wixAdapter.navigateTo.bind(this.wixAdapter);
    };
    ViewerScriptHandler.prototype.initAppForPage = function () {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var getCsrfToken, ravenClient, applicationFields, platformAPI, wixAPI, bi, _c, platformServices;
            var _this = this;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        getCsrfToken = function () { return getCookie('XSRF-TOKEN'); };
                        ravenClient = new Raven.Client();
                        return [4 /*yield*/, this.wixAdapter.getApplicationFields(this.appParams.appDefinitionId)];
                    case 1:
                        applicationFields = _d.sent();
                        this.appParams.baseUrls = ((_b = (_a = this.overrides) === null || _a === void 0 ? void 0 : _a.platform) === null || _b === void 0 ? void 0 : _b.shouldMergeBaseUrls) ? _.merge(_.get(applicationFields, 'platform.baseUrls'), _.get(this.overrides, 'platform.baseUrls'))
                            : _.get(this.overrides, 'platform.baseUrls', _.get(applicationFields, 'platform.baseUrls'));
                        platformAPI = {
                            pubSub: this.wixAdapter.getPubSub(),
                            storage: this.getStorage(),
                            getCsrfToken: getCsrfToken,
                        };
                        this.wixAPI.window.viewMode = this.wixAdapter.getViewMode();
                        wixAPI = this.wixAPI;
                        bi = this.wixAdapter.getBiObj();
                        if (!this.overrides.createCentralizedExperiments) return [3 /*break*/, 3];
                        _c = this;
                        return [4 /*yield*/, this.overrides.createCentralizedExperiments({
                                ctx: {
                                    siteOwnerId: bi.ownerId,
                                    loggedInUserId: wixAPI.user.currentUser.id,
                                },
                            })];
                    case 2:
                        _c.centralizedExperiments = _d.sent();
                        this.overrides.essentials.experiments = this.centralizedExperiments;
                        _d.label = 3;
                    case 3:
                        platformServices = {
                            essentials: this.overrides.essentials,
                            bi: bi,
                            monitoring: {
                                createMonitor: function (target) {
                                    ravenClient.config(target, { environment: 'Editor-mode wrapper' });
                                    return ravenClient;
                                },
                            },
                            getCsrfToken: getCsrfToken,
                            fedOpsLoggerFactory: {
                                getLoggerForWidget: function (config) {
                                    if (_this.overrides.fedopsLogger) {
                                        return _this.overrides.fedopsLogger.create(config.fedopsAppName || _this.overrides.fedopsAppName || config.appId + "_" + config.widgetId + "_iframe");
                                    }
                                    return {
                                        appLoadStarted: _.noop,
                                        appLoaded: _.noop,
                                        appLoadingPhaseFinish: _.noop,
                                        appLoadingPhaseStart: _.noop,
                                        reportErrorThrownOncePerFlow: _.noop,
                                        clearResourceTimings: _.noop,
                                        flush: _.noop,
                                        interactionEnded: _.noop,
                                        interactionStarted: function () { return ({ timeoutId: 0 }); },
                                    };
                                },
                            },
                            biLoggerFactory: function () { return webLogger; },
                        };
                        this.platformAPIs = _.merge({}, platformAPI, platformServices);
                        if (_.isFunction(this.viewerScript.initAppForPage)) {
                            return [2 /*return*/, this.viewerScript.initAppForPage(this.appParams, platformAPI, wixAPI, platformServices)];
                        }
                        return [2 /*return*/, Promise.resolve()];
                }
            });
        });
    };
    ViewerScriptHandler.prototype.getInstance = function () {
        return _.get(this.overrides, 'instance', getUrlParam(paramNames.INSTANCE));
    };
    ViewerScriptHandler.prototype.updateController = function (newConfig) {
        if (this.controller && _.isFunction(this.controller.updateConfig)) {
            return this.controller.updateConfig(this.$wWrapper, newConfig);
        }
    };
    ViewerScriptHandler.prototype.onBeforeUnLoad = function () {
        if (this.controller && _.isFunction(this.controller.onBeforeUnLoad)) {
            return this.controller.onBeforeUnLoad();
        }
    };
    ViewerScriptHandler.prototype.getWarmUpData = function () {
        return null;
    };
    ViewerScriptHandler.prototype.initControllers = function (widgetConfig, config, compId) {
        return __awaiter(this, void 0, void 0, function () {
            var warmupData, controllerEssentials, controllers, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        warmupData = this.getWarmUpData();
                        controllerEssentials = this.overrides.controllerEssentials;
                        if (controllerEssentials && this.centralizedExperiments) {
                            controllerEssentials.experiments = this.centralizedExperiments;
                        }
                        controllers = this.viewerScript.createControllers([
                            {
                                type: widgetConfig.widgetId,
                                config: config,
                                compId: compId,
                                setProps: this.setProps,
                                $w: this.$wWrapper,
                                warmupData: warmupData,
                                appParams: this.appParams,
                                platformAPIs: this.platformAPIs,
                                wixCodeApi: this.wixAPI,
                                essentials: controllerEssentials,
                            },
                        ]);
                        if (!controllers[0]) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, controllers[0]];
                    case 1:
                        _a.controller = _b.sent();
                        return [2 /*return*/, this.controller.pageReady(this.$wWrapper, this.wixAPI)];
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    return ViewerScriptHandler;
}());
export { ViewerScriptHandler };
//# sourceMappingURL=viewerScriptHandler.js.map